/**
 * TGDataTable
 * Advanced data grid with filters, sorting, column visibility, and export
 */

import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Space } from 'antd';
import { ReloadOutlined, ClearOutlined, FilterOutlined } from '@ant-design/icons';
import TGColumnConfigurator from './TGColumnConfigurator';
import TGExportMenu from './TGExportMenu';
import TGQuickFilters from './TGQuickFilters';
import TGQuickSearch from './TGQuickSearch';
import TGPresetManager from './TGPresetManager';
import useTGDataTable from './useTGDataTable';
import { spacing, colors, typography } from '../../../theme/designTokens';
import EmptyState from '../../layout/EmptyState';

/**
 * @param {import('./types').TGDataTableProps} props
 */
function TGDataTable({
  columns = [],
  data = [],
  rowKey = 'id',
  total,
  loading = false,
  pagination = 'client',
  pageSize: defaultPageSize = 10,
  onQueryChange,
  presetKey,
  toolbar,
  showExport = true,
  showColumnConfig = true,
  showFilters = true,
  showQuickSearch = false,
  showPresetManager = false,
  fetchAll,
  transformForExport,
  exportFilename = 'export',
  style = {},
  onRefresh,
  onLoadMore, // For infinite scroll mode
  externalFilterCount = 0, // Count of filters outside TGDataTable (e.g., date range)
  onClearExternalFilters, // Callback to clear external filters
  rowSelection, // Row selection config (multi-select checkboxes)
  ...restProps
}) {
  // Initialize hook with all column keys visible by default
  const defaultVisibleColumns = columns.map((col) => col.key);
  const defaultColumnOrder = columns.map((col) => col.key);

  const tableState = useTGDataTable({
    presetKey,
    defaultPageSize,
    defaultVisibleColumns,
    defaultColumnOrder,
  });

  const {
    query,
    page,
    pageSize: currentPageSize,
    sort,
    filters,
    visibleColumns,
    columnOrder,
    setPage,
    setPageSize,
    setSort,
    setFilter,
    setFilters,
    clearFilters,
    setVisibleColumns,
    setColumnOrder,
    reorderColumns,
    reset,
  } = tableState;

  // Quick search state
  const [quickSearchValue, setQuickSearchValue] = React.useState('');

  // Filter visibility state
  const [filtersVisible, setFiltersVisible] = React.useState(false);

  // Infinite scroll observer
  const infiniteScrollRef = useRef(null);
  const loadingMoreRef = useRef(false);

  // Emit query changes for server mode
  useEffect(() => {
    if (pagination === 'server' && onQueryChange) {
      onQueryChange(query);
    }
  }, [query, pagination, onQueryChange]);

  // Filter and order columns
  const displayColumns = useMemo(() => {
    const visible = columns.filter((col) => visibleColumns.includes(col.key));

    // Apply column order if specified
    if (columnOrder && columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((key, idx) => [key, idx]));
      return visible.sort((a, b) => {
        const aOrder = orderMap.has(a.key) ? orderMap.get(a.key) : 999;
        const bOrder = orderMap.has(b.key) ? orderMap.get(b.key) : 999;
        return aOrder - bOrder;
      });
    }

    return visible;
  }, [columns, visibleColumns, columnOrder]);

  // Apply client-side quick search
  const quickSearchedData = useMemo(() => {
    if (!quickSearchValue || pagination === 'server') return data;

    const searchLower = quickSearchValue.toLowerCase();
    return data.filter((row) => {
      return displayColumns.some((col) => {
        const value = row[col.dataIndex];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, quickSearchValue, displayColumns, pagination]);

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    if (pagination === 'server') return quickSearchedData; // Server handles filtering

    let result = [...quickSearchedData];

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      const col = columns.find((c) => c.key === key);
      if (!col || !value) return;

      result = result.filter((row) => {
        const rowValue = row[col.dataIndex];

        switch (col.filterType) {
          case 'text':
            return String(rowValue || '')
              .toLowerCase()
              .includes(String(value).toLowerCase());

          case 'number':
            return rowValue === value;

          case 'select':
            return rowValue === value;

          case 'multiselect':
            if (!Array.isArray(value) || value.length === 0) return true;
            // For array row values (like pii_types), check if any selected value is in the array
            if (Array.isArray(rowValue)) {
              return rowValue.some((rv) => value.includes(rv));
            }
            // For single row values, check if it's in the selected values
            return value.includes(rowValue);

          case 'range':
            if (value.min !== undefined && rowValue < value.min) return false;
            if (value.max !== undefined && rowValue > value.max) return false;
            return true;

          case 'date':
          case 'dateRange':
            // Date filtering would need more sophisticated logic
            return true;

          default:
            return true;
        }
      });
    });

    return result;
  }, [data, filters, columns, pagination]);

  // Apply client-side sorting
  const sortedData = useMemo(() => {
    if (pagination === 'server') return filteredData; // Server handles sorting
    if (!sort) return filteredData;

    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[col.dataIndex];
      const bVal = b[col.dataIndex];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sort.order === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredData, sort, columns, pagination]);

  // Apply client-side pagination
  const paginatedData = useMemo(() => {
    if (pagination !== 'client') return sortedData;

    const start = (page - 1) * currentPageSize;
    const end = start + currentPageSize;
    return sortedData.slice(start, end);
  }, [sortedData, page, currentPageSize, pagination]);

  // Final data to display
  const displayData = pagination === 'client' ? paginatedData : data;

  // Build AntD columns with sort handlers
  const antdColumns = useMemo(() => {
    return displayColumns.map((col) => ({
      key: col.key,
      title: col.title,
      dataIndex: col.dataIndex,
      width: col.width,
      align: col.align,
      fixed: col.fixed,
      render: col.render,
      sorter: col.sortable ? true : false,
      sortOrder:
        sort?.key === col.key ? (sort.order === 'asc' ? 'ascend' : 'descend') : null,
    }));
  }, [displayColumns, sort]);

  // Infinite scroll setup
  useEffect(() => {
    if (pagination !== 'infinite' || !onLoadMore || !infiniteScrollRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const lastRow = entries[0];
        if (lastRow.isIntersecting && !loading && !loadingMoreRef.current) {
          loadingMoreRef.current = true;
          onLoadMore().finally(() => {
            loadingMoreRef.current = false;
          });
        }
      },
      { threshold: 0.5 }
    );

    const currentRef = infiniteScrollRef.current;
    observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [pagination, onLoadMore, loading]);

  // Pagination config
  const paginationConfig =
    pagination === 'none' || pagination === 'infinite'
      ? false
      : {
          current: page,
          pageSize: currentPageSize,
          total: pagination === 'server' ? total : sortedData.length,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            if (newPageSize !== currentPageSize) {
              setPageSize(newPageSize);
              setPage(1); // Reset to first page when page size changes
            }
          },
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          pageSizeOptions: ['10', '20', '50', '100'],
        };

  // Handle table change (sort)
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      const col = columns.find((c) => c.dataIndex === sorter.field);
      if (col) {
        setSort({
          key: col.key,
          order: sorter.order === 'ascend' ? 'asc' : 'desc',
        });
      }
    } else {
      setSort(null);
    }
  };

  // Active filter count (include external filters)
  const activeFilterCount = Object.keys(filters).length + (quickSearchValue ? 1 : 0) + externalFilterCount;

  // Clear all filters including external and collapse filter bar
  const handleClearAll = () => {
    clearFilters();
    if (onClearExternalFilters) {
      onClearExternalFilters();
    }
    setFiltersVisible(false);
  };

  // Load preset handler
  const handleLoadPreset = useCallback(
    (presetState) => {
      if (presetState.filters) setFilters(presetState.filters);
      if (presetState.visibleColumns) setVisibleColumns(presetState.visibleColumns);
      if (presetState.columnOrder) setColumnOrder(presetState.columnOrder);
      if (presetState.sort) setSort(presetState.sort);
      if (presetState.pageSize) setPageSize(presetState.pageSize);
      setPage(1);
    },
    [setFilters, setVisibleColumns, setColumnOrder, setSort, setPageSize, setPage]
  );

  // Toolbar
  const toolbarContent = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: spacing.md,
      }}
    >
      <Space size="small">
        {showFilters && (
          <Button
            icon={<FilterOutlined />}
            onClick={() => setFiltersVisible(!filtersVisible)}
            type={activeFilterCount > 0 ? 'primary' : 'default'}
            style={
              activeFilterCount > 0
                ? { backgroundColor: 'rgb(75, 55, 129)', borderColor: 'rgb(75, 55, 129)' }
                : {}
            }
          >
            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        )}
        {activeFilterCount > 0 && (
          <Button type="link" onClick={handleClearAll} style={{ color: 'rgb(75, 55, 129)' }}>
            Clear
          </Button>
        )}
      </Space>

      <Space size="small">
        {toolbar}
        {onRefresh && (
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            Refresh
          </Button>
        )}
        {showPresetManager && presetKey && (
          <TGPresetManager
            presetKey={presetKey}
            currentState={query}
            onLoadPreset={handleLoadPreset}
          />
        )}
        {showExport && (
          <TGExportMenu
            data={pagination === 'client' ? sortedData : displayData}
            currentPageData={paginatedData}
            columns={displayColumns}
            allColumns={columns}
            fetchAll={fetchAll}
            transformForExport={transformForExport}
            filename={exportFilename}
            isPaginatedClient={pagination === 'client'}
          />
        )}
        {showColumnConfig && (
          <TGColumnConfigurator
            columns={columns}
            visibleColumns={visibleColumns}
            columnOrder={columnOrder}
            onChange={setVisibleColumns}
            onReorder={reorderColumns}
            onReset={reset}
          />
        )}
      </Space>
    </div>
  );

  // Custom empty state
  const locale = {
    emptyText: (
      <EmptyState
        title="No Data"
        description={
          activeFilterCount > 0
            ? 'No results match your filters. Try adjusting or clearing filters.'
            : 'No data available'
        }
      />
    ),
  };

  return (
    <div style={style}>
      {showQuickSearch && (
        <TGQuickSearch value={quickSearchValue} onChange={setQuickSearchValue} />
      )}

      {toolbarContent}

      <Table
        columns={antdColumns}
        dataSource={displayData}
        rowKey={rowKey}
        loading={loading}
        pagination={paginationConfig}
        onChange={handleTableChange}
        locale={locale}
        size="middle"
        rowSelection={rowSelection}
        {...restProps}
        // Add filter row if enabled and visible
        components={
          showFilters && filtersVisible
            ? {
                header: {
                  row: ({ children, ...props }) => (
                    <>
                      <tr {...props}>{children}</tr>
                      <TGQuickFilters
                        columns={displayColumns}
                        filters={filters}
                        onFilterChange={setFilter}
                      />
                    </>
                  ),
                },
              }
            : undefined
        }
      />

      {/* Infinite scroll sentinel */}
      {pagination === 'infinite' && (
        <div
          ref={infiniteScrollRef}
          style={{
            height: 20,
            textAlign: 'center',
            padding: spacing.md,
            color: 'rgba(0,0,0,0.45)',
          }}
        >
          {loading ? 'Loading more...' : ''}
        </div>
      )}
    </div>
  );
}

export default TGDataTable;
